import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { heroSlides, statsData as defaultStatsData, stories as mockStories } from '../../mocks/homeData';
import { fetchPublicDynamics, fetchDropsDynamics, fetchPublicFundOverview } from '../../api/dripPlan';
import type { DynamicItem } from '../../api/dripPlan';

const MAX_FRONTLINE_STORIES = 4;

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [statsData, setStatsData] = useState(defaultStatsData);
  const [animatedStats, setAnimatedStats] = useState(defaultStatsData.map(() => 0));
  const [stories, setStories] = useState<typeof mockStories>(mockStories);
  const [dropsDynamics, setDropsDynamics] = useState<DynamicItem[]>([]);
  const [dropsDynamicsLoading, setDropsDynamicsLoading] = useState(true);

  useEffect(() => {
    fetchPublicDynamics()
      .then((list) => {
        setStories(Array.isArray(list) ? list : mockStories);
      })
      .catch(() => {
        setStories(mockStories);
      });
    setDropsDynamicsLoading(true);
    fetchDropsDynamics()
      .then((list) => {
        setDropsDynamics(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setDropsDynamics([]);
      })
      .finally(() => setDropsDynamicsLoading(false));
  }, []);

  useEffect(() => {
    fetchPublicFundOverview()
      .then((overview) => {
        if (!overview) return;
        setStatsData([
          {
            id: 1,
            icon: 'ri-heart-fill',
            number: overview.donationCount || 0,
            unit: '人次',
            label: '累计捐赠人次',
          },
          {
            id: 2,
            icon: 'ri-water-flash-fill',
            number: overview.totalBottlesDisbursed || 0,
            unit: '瓶',
            label: '累计发放水瓶数',
          },
          {
            id: 3,
            icon: 'ri-map-pin-fill',
            number: overview.regionCount || 0,
            unit: '个区县',
            label: '覆盖区域',
          },
        ]);
        setAnimatedStats([0, 0, 0]);
      })
      .catch(() => {});
  }, []);

  // Auto slide
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Animate stats on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            statsData.forEach((stat, index) => {
              let start = 0;
              const end = stat.number;
              const duration = 2000;
              const increment = end / (duration / 16);

              const timer = setInterval(() => {
                start += increment;
                if (start >= end) {
                  start = end;
                  clearInterval(timer);
                }
                setAnimatedStats((prev) => {
                  const newStats = [...prev];
                  newStats[index] = Math.floor(start);
                  return newStats;
                });
              }, 16);
            });
          }
        });
      },
      { threshold: 0.3 }
    );

    const statsElement = document.getElementById('stats-section');
    if (statsElement) observer.observe(statsElement);

    return () => observer.disconnect();
  }, [statsData]);

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <Navbar />

      {/* Hero Slider - 移动端适配 */}
      <section className="relative h-[60vh] md:h-[calc(100vh-80px)] mt-20 overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="max-w-[1400px] w-full px-6">
                <div className="max-w-2xl">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-white/20 backdrop-blur-sm rounded-full border border-[#2A7A62] mb-4 md:mb-6">
                    <i className="ri-shield-check-line text-[#2A7A62] text-sm md:text-base"></i>
                    <span className="text-white text-xs md:text-sm font-medium">公益备案项目</span>
                  </div>

                  <h1 className="text-2xl md:text-6xl lg:text-7xl font-bold text-white mb-2 md:mb-4 animate-[fadeInUp_0.8s_ease-out]">
                    {slide.title}
                  </h1>
                  <p className="text-lg md:text-4xl lg:text-5xl font-light text-white/90 mb-3 md:mb-6 italic animate-[fadeInUp_1s_ease-out]">
                    {slide.subtitle}
                  </p>
                  <p className="text-sm md:text-xl text-white/80 mb-4 md:mb-8 leading-relaxed animate-[fadeInUp_1.2s_ease-out]">
                    {slide.description}
                  </p>

                  <div className="flex flex-wrap gap-3 md:gap-4 animate-[fadeInUp_1.4s_ease-out]">
                    <Link
                      to="/donate"
                      className="px-4 py-2 md:px-8 md:py-4 bg-[#2A7A62] text-white text-sm md:text-lg font-medium rounded-lg hover:bg-[#236854] transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 cursor-pointer whitespace-nowrap"
                    >
                      我要捐赠
                    </Link>
                    <Link
                      to="/apply"
                      className="px-4 py-2 md:px-8 md:py-4 bg-white text-[#2A7A62] text-sm md:text-lg font-medium rounded-lg border-2 border-[#2A7A62] hover:bg-[#2A7A62]/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      申请帮助
                    </Link>
                  </div>

                  <p className="mt-4 md:mt-6 text-white/70 text-xs md:text-base">
                    <span className="text-[#F4B942] font-bold text-base md:text-xl">19.9元</span> = 12瓶水
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Slide Indicators */}
        <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 flex gap-2 md:gap-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`transition-all cursor-pointer ${
                index === currentSlide
                  ? 'w-8 md:w-12 h-1.5 md:h-2 bg-[#2A7A62]'
                  : 'w-6 md:w-8 h-1.5 md:h-2 bg-white/50 hover:bg-white/80'
              } rounded-full`}
            ></button>
          ))}
        </div>
      </section>

      {/* Stats Section - 移动端三个长方形板块竖排 */}
      <section id="stats-section" className="py-12 md:py-20 bg-gradient-to-b from-[#F9F9F9] to-[#E8F4F0]">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="bg-white rounded-2xl md:rounded-[32px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-6 md:p-16">
            {/* 移动端和平板端：三个长方形板块竖排 */}
            <div className="flex flex-col gap-4 lg:hidden">
              {statsData.map((stat, index) => (
                <div 
                  key={stat.id} 
                  className="flex items-center gap-4 p-4 bg-gradient-to-r from-[#E8F4F0] to-[#F9F9F9] rounded-xl"
                >
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-[#2A7A62] to-[#3A9A82] rounded-xl flex-shrink-0">
                    <i className={`${stat.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-2xl font-bold text-[#2A7A62] tabular-nums">
                        {animatedStats[index].toLocaleString()}
                      </span>
                      <span className="text-sm text-[#6C6C6C]">{stat.unit}</span>
                    </div>
                    <p className="text-sm text-[#6C6C6C]">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* PC端：原有的三列布局 */}
            <div className="hidden lg:grid lg:grid-cols-3 gap-12">
              {statsData.map((stat, index) => (
                <div key={stat.id} className="text-center relative flex flex-col items-center">
                  <div className="flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#2A7A62] to-[#3A9A82] rounded-2xl mb-6 animate-[spin_3s_linear_infinite] flex-shrink-0">
                    <i className={`${stat.icon} text-4xl text-white`}></i>
                  </div>
                  <div className="text-center">
                    <div className="flex flex-row items-baseline justify-center gap-2 mb-3">
                      <span className="text-6xl font-bold text-[#2A7A62] tabular-nums">
                        {animatedStats[index].toLocaleString()}
                      </span>
                      <span className="text-2xl text-[#6C6C6C]">{stat.unit}</span>
                    </div>
                    <p className="text-base text-[#6C6C6C] flex items-center justify-center gap-2">
                      <i className="ri-checkbox-circle-line text-[#2A7A62]"></i>
                      {stat.label}
                    </p>
                  </div>
                  {index < statsData.length - 1 && (
                    <div className="absolute top-1/2 -right-6 w-12 h-0.5 bg-gradient-to-r from-[#2A7A62] to-transparent"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#E8F4F0] to-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="lg:col-span-7 space-y-4 md:space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2A7A62] text-white rounded-lg">
                <span className="text-xs md:text-sm font-medium">/ 关于点滴公益</span>
              </div>

              <h2 className="text-3xl md:text-5xl font-bold text-[#1E1E1E] leading-tight">
                用点滴爱心<br />汇聚温暖力量
              </h2>

              <div className="space-y-4 md:space-y-6">
                <p className="text-base md:text-xl text-[#1E1E1E] leading-relaxed">
                  "点滴公益"是由重庆市南岸区明明德志愿服务中心发起的公益项目,旨在为城市一线工作者提供夏日清凉关怀。
                </p>
                <p className="text-sm md:text-lg text-[#1E1E1E] leading-relaxed">
                  我们服务的对象包括<span className="text-[#E06B3D] font-semibold">环卫工人</span>、
                  <span className="text-[#E06B3D] font-semibold">快递员</span>、
                  <span className="text-[#E06B3D] font-semibold">建筑工人</span>、
                  <span className="text-[#E06B3D] font-semibold">交通协管员</span>
                  等在高温下坚守岗位的城市守护者。
                </p>
                <p className="text-sm md:text-lg text-[#1E1E1E] leading-relaxed flex items-start gap-2 md:gap-3">
                  <i className="ri-shield-check-line text-[#2A7A62] text-lg md:text-2xl mt-1"></i>
                  <span>
                    我们承诺100%透明捐赠,每一笔善款的使用都会在公益公示页面详细公开,
                    接受社会监督,让您的爱心真正送达需要的人手中。
                  </span>
                </p>
              </div>

              <Link
                to="/project"
                className="inline-flex items-center gap-2 text-[#2A7A62] text-base md:text-lg font-medium hover:gap-4 transition-all cursor-pointer group"
              >
                了解更多
                <i className="ri-arrow-right-line text-lg md:text-xl group-hover:translate-x-2 transition-transform"></i>
              </Link>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl md:rounded-3xl overflow-hidden border-2 border-[#2A7A62] shadow-2xl">
                <img
                  src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 1000'%3E%3Crect fill='%23e5e7eb' width='800' height='1000'/%3E%3C/svg%3E"
                  alt="志愿者送水"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 bg-white rounded-xl md:rounded-2xl px-4 py-3 md:px-6 md:py-4 shadow-xl">
                  <p className="text-lg md:text-2xl font-bold text-[#2A7A62]">已帮助</p>
                  <p className="text-2xl md:text-4xl font-bold text-[#1E1E1E]">10,000+</p>
                  <p className="text-sm md:text-base text-[#6C6C6C]">一线工作者</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stories Section */}
      <section className="py-12 md:py-20 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-[#2A7A62] mb-2 md:mb-4">一线故事</h2>
            <p className="text-lg md:text-2xl text-[#6C6C6C] font-light mb-2">真实记录每一份温暖</p>
            <p className="text-sm md:text-base text-[#6C6C6C] max-w-2xl mx-auto">
              这里汇集了来自城市各个角落的真实故事，记录着环卫工人、快递员、建筑工人等一线工作者的日常与感动
            </p>
          </div>

          <div className="relative">
            <div className="overflow-x-auto md:overflow-visible scrollbar-hide">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 pb-4">
                {stories.slice(0, MAX_FRONTLINE_STORIES).map((story) => (
                  <Link
                    key={story.id}
                    to={`/story/${story.id}`}
                    className="bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="relative h-32 md:h-[240px] overflow-hidden">
                      <img
                        src={story.image}
                        alt={story.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 right-2 md:top-4 md:right-4 px-2 py-1 md:px-4 md:py-2 bg-[#F4B942] text-white text-xs md:text-sm font-medium rounded-full">
                        {story.category}
                      </div>
                    </div>
                    <div className="p-3 md:p-6 space-y-2 md:space-y-4">
                      <h3 className="text-sm md:text-2xl font-bold text-[#1E1E1E] line-clamp-2">
                        {story.title}
                      </h3>
                      <p className="text-xs md:text-base text-[#6C6C6C] leading-relaxed line-clamp-2 md:line-clamp-3 hidden md:block">
                        {story.excerpt}
                      </p>
                      <div className="flex items-center justify-between pt-2 md:pt-4 border-t border-[#E0E0E0]">
                        <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm text-[#6C6C6C]">
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line"></i>
                            <span className="hidden md:inline">{story.date || '—'}</span>
                            <span className="md:hidden">{story.date ? story.date.slice(5) : '—'}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            <span className="hidden md:inline">{story.views.toLocaleString()}</span>
                            <span className="md:hidden">{(story.views / 1000).toFixed(1)}k</span>
                          </span>
                        </div>
                        <span className="text-[#2A7A62] font-medium flex items-center gap-1 group-hover:gap-2 transition-all text-xs md:text-sm">
                          阅读
                          <i className="ri-arrow-right-line hidden md:inline"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 点滴动态 - 来自动态发布（展示位置=点滴动态） */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-[#2A7A62] mb-2 md:mb-4">点滴动态</h2>
            <p className="text-lg md:text-2xl text-[#6C6C6C] font-light mb-2">记录点滴，传递温暖</p>
            <p className="text-sm md:text-base text-[#6C6C6C] max-w-2xl mx-auto">
              了解项目最新进展与公益活动动态
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 pb-4">
            {dropsDynamicsLoading ? (
              [1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="bg-[#F9F9F9] rounded-xl md:rounded-2xl overflow-hidden shadow-lg min-h-[200px] flex items-center justify-center border border-[#E0E0E0] animate-pulse"
                >
                  <span className="text-[#6C6C6C] text-sm">加载中...</span>
                </div>
              ))
            ) : dropsDynamics.length > 0 ? (
              dropsDynamics.slice(0, 4).map((item) => (
                <Link
                  key={item.id}
                  to={`/story/${item.id}`}
                  className="bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer group border border-[#E0E0E0]"
                >
                  <div className="relative h-32 md:h-[200px] overflow-hidden bg-[#F9F9F9]">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#6C6C6C]">
                        <i className="ri-file-text-line text-4xl"></i>
                      </div>
                    )}
                    {item.category ? (
                      <div className="absolute top-2 right-2 md:top-3 md:right-3 px-2 py-1 md:px-3 md:py-1.5 bg-[#2A7A62] text-white text-xs font-medium rounded-full">
                        {item.category}
                      </div>
                    ) : null}
                  </div>
                  <div className="p-3 md:p-4 space-y-2">
                    <h3 className="text-sm md:text-lg font-bold text-[#1E1E1E] line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-xs md:text-sm text-[#6C6C6C] leading-relaxed line-clamp-2 hidden md:block">
                      {item.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-2 border-t border-[#E0E0E0] text-xs text-[#6C6C6C]">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {item.date || '—'}
                      </span>
                      <span className="text-[#2A7A62] font-medium flex items-center gap-1">
                        阅读
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              [1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="bg-[#F9F9F9] rounded-xl md:rounded-2xl overflow-hidden shadow-lg min-h-[200px] flex items-center justify-center border border-[#E0E0E0]"
                >
                  <span className="text-[#6C6C6C] text-sm">敬请期待</span>
                </div>
              ))
            )}
          </div>
          {!dropsDynamicsLoading && dropsDynamics.length > 0 && (
            <div className="text-center mt-6 md:mt-8">
              <Link
                to="/drops-dynamics"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2A7A62] text-white font-medium hover:bg-[#238B6F] transition-colors shadow-lg hover:shadow-xl"
              >
                更多点滴动态
                <i className="ri-arrow-right-line text-lg"></i>
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
